import { useNavigate } from "react-router-dom";
import { Briefcase, CalendarDays, Users, KanbanSquare, Newspaper, ShieldCheck, Sparkles, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: CalendarDays, title: "Scheduling that checks itself", text: "The CEO finds a slot, looks for clashes with meetings and approved leave, then puts the meeting forward for your approval." },
  { icon: Users, title: "HR without the admin", text: "Staff directory, leave requests and onboarding checklists, with suggested decisions for each." },
  { icon: KanbanSquare, title: "Tasks and goals that move", text: "A kanban board linked to company goals. Overdue work is flagged and reassigned with one approval." },
  { icon: Newspaper, title: "A daily CEO briefing", text: "Each morning you get a short summary of what needs your attention, who's out and what's at risk." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <header className="max-w-[1200px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Briefcase className="h-4 w-4 text-white" /></div>
          <span className="font-display font-semibold text-lg">Virtual CEO</span>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="ghost" onClick={() => navigate("/pricing")} data-testid="landing-pricing-link">Pricing</Button>
          <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-button">Sign in</Button>
          <Button onClick={() => navigate("/signup")} data-testid="landing-signup-button">Get started</Button>
        </div>
      </header>

      <section className="hero-gradient noise">
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 py-14 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-medium bg-card border border-border rounded-full px-3 py-1 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> AI proposes, you approve
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              A virtual CEO that keeps your business running.
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
              Scheduling, HR and day-to-day operations in one workspace. Your AI CEO reads your company's data, tells you what matters, and lines up the next steps for you to approve.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate("/signup")} data-testid="landing-primary-cta">
                Start your workspace <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Button size="lg" variant="secondary" onClick={() => navigate("/pricing")} data-testid="landing-pricing-cta">
                See pricing
              </Button>
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
              {["A separate private workspace for each company", "Invite your team with a code", "Every AI action needs a human to approve it", "Sign in with Google or email"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-[hsl(var(--success))]" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/7212946/pexels-photo-7212946.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Team working together"
              className="rounded-2xl border border-border shadow-lg w-full object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-6 left-4 right-4 sm:left-8 sm:right-auto sm:w-[340px] bg-card border border-border border-l-4 border-l-[hsl(var(--warning))] rounded-xl p-4 shadow-lg">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Leave decision · proposed by AI CEO</p>
              <p className="text-sm font-semibold mt-0.5">Approve leave: Diego Ramirez</p>
              <p className="text-xs text-muted-foreground mt-1">No clashes with deadlines, and Sara is covering the handover.</p>
              <div className="mt-3 flex gap-2">
                <span className="text-xs bg-primary text-white rounded-md px-2.5 py-1">Approve</span>
                <span className="text-xs bg-secondary rounded-md px-2.5 py-1">Reject</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight max-w-2xl">Everything a CEO checks every day, handled in one place.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center mb-4"><Icon className="h-5 w-5" /></div>
              <p className="font-semibold">{title}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-6">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-card border-y border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <img src="https://images.pexels.com/photos/12902903/pexels-photo-12902903.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Team planning" className="rounded-2xl border border-border w-full object-cover aspect-[16/10]" />
          <div>
            <ShieldCheck className="h-8 w-8 text-primary" />
            <h3 className="text-2xl font-semibold mt-4">You stay in control</h3>
            <p className="text-muted-foreground mt-3 leading-7">
              The AI CEO never changes anything on its own. Each meeting, task, leave decision or announcement shows up as a card you approve or reject, and the decision is logged with your name. Owners and managers approve. Employees can approve only their own meetings and tasks.
            </p>
            <Button className="mt-6" onClick={() => navigate("/signup")} data-testid="landing-bottom-cta">Create your workspace</Button>
          </div>
        </div>
      </section>
      <footer className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 text-xs text-muted-foreground">© {new Date().getFullYear()} Virtual CEO</footer>
    </div>
  );
}
